import type { CSSProperties } from 'react'

export interface StatusTagProps {
    /** Підпис статусу — той самий, що в колонці. */
    label: string

    /** Колір статусу з `*_STATUS_MAP`. Лише hex: інші формати ядро не розбирає. */
    color?: string | undefined

    /** Рядкова подача: бейдж усередині тексту, а не окремим блоком. */
    inline?: boolean
}

/** Колір, яким ядро малює статус без власного кольору. */
const FALLBACK = '#8C8C8C'

/**
 * Розкласти hex у складові.
 *
 * ⚠️ Рецепт узятий із ядра (`Features/UI/CrudResource/utils.ts`, гілка
 * `case 'status'`) **побайтово**, разом із запасним сірим: бейдж у тексті
 * мусить збігатися з бейджем у колонці до відтінку, інакше користувач
 * вирішить, що це різні речі.
 *
 * Звідси й обмеження на hex: ядро розбирає колір саме так, і назва на кшталт
 * `green` мовчки дає сірий. Тому кольори в `*_STATUS_MAP` — hex.
 */
const rgb = (hex: string): { r: number; g: number; b: number } => {
    let clean = hex.replace(/^#/, '').substring(0, 6)

    if (clean.length === 3) {
        clean = clean
            .split('')
            .map((char) => char + char)
            .join('')
    }

    const parsed = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(clean)

    if (!parsed) return { r: 140, g: 140, b: 140 }

    return {
        r: parseInt(parsed[1]!, 16),
        g: parseInt(parsed[2]!, 16),
        b: parseInt(parsed[3]!, 16),
    }
}

/**
 * Бейдж статусу — один на весь застосунок.
 *
 * ⚠️ Існує рівно заради однаковості. Того самого статусу було **три різні
 * вигляди**: ядровий бейдж у колонці (`rgba(…, .85)`, білий текст), антдшний
 * `<Tag color="#008000">` у картці (суцільний фон) і `<Tag color="green">` у
 * документах руху (світлий фон, темний текст). Для того, хто дивиться, це
 * читається як три різні сутності.
 *
 * ⚠️ Клас власний, і обидва «очевидні» імена зайняті:
 *
 *   • `status-badge-table` — ним ядро малює саме клітинку таблиці, і e2e
 *     перевіряють ним колонку. Чужий бейдж під тим самим іменем зламав би ті
 *     перевірки: вони почали б ловити картку замість колонки;
 *   • `status-badge` — ним `Ship/Components/FormFields/StatusInput` малює
 *     статус у формі, і в його CSS лежить `text-transform: uppercase` з іншою
 *     геометрією. Клас глобальний, тож перше ж використання дало «ЗАВЕРШЕНЕ»
 *     замість «Завершене» — тобто рівно те, чого ми тут уникаємо.
 */
export const StatusTag = ({ label, color, inline = false }: StatusTagProps) => {
    const { r, g, b } = rgb(color ?? FALLBACK)

    const style: CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.85)`,
        color: '#FFFFFF',
        border: `1px solid rgba(${r}, ${g}, ${b}, 1)`,
        borderRadius: 4,
        padding: '2px 8px',
        fontSize: 14,
        fontWeight: 400,
        lineHeight: 1.4,
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
        // Усередині речення бейдж вирівнюємо по базовій лінії й **не** даємо
        // йому полів: пробіли навколо вже є в самому тексті, а зайве праве
        // поле відсувало крапку — виходило «Завершене .»
        ...(inline ? { verticalAlign: 'baseline' } : {}),
    }

    return (
        <span className="wl-status-tag" style={style}>
            {label}
        </span>
    )
}
